// MainMenu.js
// Holding all the buttons.

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../../../store/appStore";
import { useUserState } from "../../../../context/user";
import { DelMenu } from "./DelMenu";
import { ReportingMenu } from "./ReportingMenu";
import { SamplingEventsMenu } from "./SamplingEventsMenu";
import { SafetyMenu } from "./SafetyMenu";
import { AdminMenu } from "./AdminMenu";
import { LogoutButton } from "../buttons/LogoutButton";
import styles from "./MainMenu.module.css";

export const MainMenu = () => {
  const navigate = useNavigate();
  const { activeTab } = useAppStore((s) => s.navigation, shallow);
  const user = useUserState();
  const {
    isMenuOpen,
    isDelMenuExpanded,
    isSafetyFormsMenuExpanded,
    isSamplingEventsMenuExpanded,
  } = useAppStore((s) => s.mainMenu, shallow);

  // App Store actions
  const toggleDelMenu = useAppStore((s) => s.mainMenuActions.toggleDelMenu);
  const toggleSamplingEventsMenu = useAppStore(
    (s) => s.mainMenuActions.toggleSamplingEventsMenu
  );
  const toggleSafetyFormsMenu = useAppStore(
    (s) => s.mainMenuActions.toggleSafetyFormsMenu
  );
  const navigateToTab = useAppStore((s) => s.navigationActions.navigateToTab);

  // Add the DelForm action
  const setShowDraftsDelForm = useAppStore(
    (s) => s.draftsTabUIActions.setShowDelForm
  );
  const draftsShowDelForm = useAppStore((s) => s.draftsTabUI.showDelForm);
  const setShowSubmitsDelForm = useAppStore(
    (s) => s.submitsTabUIActions.setShowDelForm
  );
  const submitsShowDelForm = useAppStore((s) => s.submitsTabUI.showDelForm);
  const setShowApprovalsDelForm = useAppStore(
    (s) => s.approvalsTabUIActions.setShowDelForm
  );
  const approvalsShowDelForm = useAppStore((s) => s.approvalsTabUI.showDelForm);
  const setShowApprovedDelForm = useAppStore(
    (s) => s.approvalsTabUIActions.setShowApprovedDelForm
  );
  const approvedShowDelForm = useAppStore(
    (s) => s.approvalsTabUI.showApprovedDelForm
  );

  // Function to collapse all submenus
  const collapseAllSubmenus = () => {
    if (isDelMenuExpanded) toggleDelMenu();
    if (isSamplingEventsMenuExpanded) toggleSamplingEventsMenu();
    if (isSafetyFormsMenuExpanded) toggleSafetyFormsMenu();
  };

  // Collapse submenus when main menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      collapseAllSubmenus();
    }
  }, [isMenuOpen]);

  // Function to handle main menu button clicks
  const handleMainMenuButtonClick = (menuType) => {
    // Close other menus first - only if they are currently expanded
    if (menuType !== "del" && isDelMenuExpanded) {
      toggleDelMenu();
    }
    if (menuType !== "samplingEvents" && isSamplingEventsMenuExpanded) {
      toggleSamplingEventsMenu();
    }
    if (menuType !== "safetyForms" && isSafetyFormsMenuExpanded) {
      toggleSafetyFormsMenu();
    }

    // Then handle the clicked menu
    switch (menuType) {
      case "del":
        toggleDelMenu();
        break;
      case "samplingEvents":
        toggleSamplingEventsMenu();
        break;
      case "safetyForms":
        toggleSafetyFormsMenu();
        break;
      default:
        // For other buttons like "Reporting" that don't have submenus
        collapseAllSubmenus();
        navigateToTab(navigate, menuType);
        break;
    }
  };

  // Enhanced tab click handler with DelForm toggle logic so that we can make sure delforms stay open if a user clicks on another del type tab in the del menu.

  const handleTabClick = (tabName) => {
    // Special handling for drafts tab
    if (tabName === "drafts" && activeTab === "drafts" && draftsShowDelForm) {
      // If clicking on drafts while already on drafts and DelForm is open, just hide DelForm
      setShowDraftsDelForm(false);
    } else if (
      tabName === "submitted" &&
      activeTab === "submitted" &&
      submitsShowDelForm
    ) {
      // If clicking on submitted while already on submitted and DelForm is open, just hide DelForm
      setShowSubmitsDelForm(false);
    } else if (
      tabName === "approvals" &&
      activeTab === "approvals" &&
      approvalsShowDelForm
    ) {
      // If clicking on approvals while already on approvals and DelForm is open, just hide DelForm
      setShowApprovalsDelForm(false);
    } else if (
      tabName === "approved" &&
      activeTab === "approved" &&
      approvedShowDelForm
    ) {
      // If clicking on approved while already on approved and DelForm is open, just hide DelForm
      setShowApprovedDelForm(false);
      // } else if (
      //   tabName === "reporting" &&
      //   activeTab === "reporting" &&
      //   showDelForm
      // ) {
      //   // If clicking on reporting while already on reporting and DelForm is open, just hide DelForm
      //   setShowReportingDelForm(false);
    } else {
      // For all other cases, navigate normally
      navigateToTab(navigate, tabName);
    }
  };

  return (
    <nav
      className={`${styles.navigation} ${isMenuOpen ? styles.expanded : ""}`}
      data-testid="main-menu"
    >
      <div className={styles.tabButtonContainer}>
        <DelMenu
          activeTab={activeTab}
          isDelMenuExpanded={isDelMenuExpanded}
          mainMenuStyles={styles}
          handleMainMenuButtonClick={handleMainMenuButtonClick}
          onTabClick={handleTabClick} // Use the enhanced handler
        />

        <SafetyMenu
          activeTab={activeTab}
          isSafetyFormsMenuExpanded={isSafetyFormsMenuExpanded}
          mainMenuStyles={styles}
          handleMainMenuButtonClick={handleMainMenuButtonClick}
          onTabClick={handleTabClick}
        />

        <SamplingEventsMenu
          activeTab={activeTab}
          isSamplingEventsMenuExpanded={isSamplingEventsMenuExpanded}
          mainMenuStyles={styles}
          handleMainMenuButtonClick={handleMainMenuButtonClick}
          onTabClick={handleTabClick}
        />

        {/* Reporting menu - only viewable on desktop screens */}
        {window.innerWidth >= 1024 && (
          <ReportingMenu
            activeTab={activeTab}
            mainMenuStyles={styles}
            handleMainMenuButtonClick={handleMainMenuButtonClick}
          />
        )}

        {/* Admin section - only for admin users */}
        {window.innerWidth >= 1024 &&
          user.currentUser &&
          user.currentUser.admin === 1 && (
            <AdminMenu
              activeTab={activeTab}
              mainMenuStyles={styles}
              handleMainMenuButtonClick={handleMainMenuButtonClick}
            />
          )}

        {/* Logout */}
        <div
          className={`${styles.logoutContainer} ${
            isMenuOpen ? styles.visible : ""
          }`}
        >
          <LogoutButton />
        </div>
      </div>
    </nav>
  );
};
